// Formattazione "pulita" dei messaggi (email/WhatsApp/DM) in entrata e in uscita:
// separa corpo / firma+note legali / messaggio citato e trasforma URL grezzi, token
// Outlook ("testo<url>", "[photo]<url>") e tracker in link leggibili. Nessun HTML iniettato.

const QUOTE_START_RES = [
  /^-{2,}\s*(original message|messaggio originale|message d'origine|ursprüngliche nachricht|mensaje original|forwarded message|messaggio inoltrato|message transféré|weitergeleitete nachricht|mensaje reenviado)\s*-{2,}\s*$/i,
  /^_{8,}\s*$/,
  /^(From|Da|De|Von)\s*:\s*.+(<[^>]+@[^>]+>|@)/i,
];
const QUOTE_OPEN_RES = [/^(il giorno|il)\s/i, /^on\s/i, /^le\s/i, /^am\s/i, /^el\s/i];
const QUOTE_CLOSE_RE = /(ha scritto|wrote|a écrit|schrieb|escribió)\s*:?\s*$/i;

const SIGNOFF_RE = /^(kind regards|best regards|warm regards|regards|best|cheers|thanks|thank you|many thanks|sincerely|yours sincerely|yours faithfully|cordiali saluti|distinti saluti|un cordiale saluto|cordialmente|saluti|a presto|grazie|cordialement|bien cordialement|bien à vous|salutations|meilleures salutations|mit freundlichen grüßen|freundliche grüße|viele grüße|beste grüße|liebe grüße|un cordial saludo|saludos|saludos cordiales|atentamente|un saludo)[\s,.!]*$/i;

const DISCLAIMER_RE = /^(informativa|confidentiality|privacy notice|disclaimer|avviso di riservatezza|le informazioni contenute|the information contained|this (e-?mail|message)|questo messaggio|questa e-?mail|ai sensi|non stampare|please consider the environment|pensa all'ambiente|think before you print|sent from my|inviato da|envoyé de mon|gesendet von|enviado desde|\[per non ricevere|per non ricevere|to unsubscribe|annulla iscrizione)/i;
const SIG_LINE_RE = /^\[(photo|image|img|logo|icon|banner|cid:[^\]]*|https?:[^\]]*)\]|^--\s*$|<(https?|mailto|tel):|^\s*(tel|phone|mobile|mob|cell|fax|t|m|p)\.?\s*[:.]?\s*\+?[\d (]/i;
const PHONE_RE = /(\+\d[\d\s().-]{7,}\d)/;

const SOCIAL_LABELS = {
  youtube: "YouTube", facebook: "Facebook", instagram: "Instagram", linkedin: "LinkedIn", twitter: "Twitter / X",
  x: "X", tiktok: "TikTok", pinterest: "Pinterest", threads: "Threads", whatsapp: "WhatsApp", strava: "Strava",
  web: "Sito web", website: "Sito web", www: "Sito web", mail: "E-mail", email: "E-mail", map: "Mappa", maps: "Mappa",
};

function looksLikeHtml(s) {
  return /<\/?(p|div|br|a|table|tr|td|span|html|body|ul|ol|li|h[1-6]|strong|em|b|i)\b[^>]*>/i.test(s);
}

function htmlToText(html) {
  if (typeof window === "undefined" || !window.DOMParser) return html.replace(/<[^>]+>/g, " ");
  const doc = new window.DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("script,style,head").forEach((n) => n.remove());
  doc.querySelectorAll("a[href]").forEach((a) => {
    const href = a.getAttribute("href") || "";
    const text = (a.textContent || "").trim();
    if (!text || text === href) a.textContent = `<${href}>`;
    else a.textContent = `${text}<${href}>`;
  });
  doc.querySelectorAll("img").forEach((img) => {
    const src = img.getAttribute("src") || "";
    img.replaceWith(doc.createTextNode(src && !src.startsWith("cid:") ? `[image]<${src}>` : ""));
  });
  doc.querySelectorAll("br").forEach((n) => n.replaceWith("\n"));
  doc.querySelectorAll("p,div,tr,li,h1,h2,h3,h4,h5,h6,blockquote,table").forEach((n) => { n.append("\n"); n.prepend("\n"); });
  return (doc.body.textContent || "").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

export function normalizeMessageText(raw) {
  let s = String(raw || "").replace(/\r\n?/g, "\n");
  if (looksLikeHtml(s)) s = htmlToText(s);
  return s;
}

function findQuoteStart(lines) {
  for (let i = 0; i < lines.length; i += 1) {
    const ln = lines[i].trim();
    if (!ln) continue;
    if (ln.startsWith(">")) {
      const rest = lines.slice(i).filter((l) => l.trim());
      if (rest.filter((l) => l.trim().startsWith(">")).length >= Math.max(1, Math.floor(rest.length * 0.5))) return i;
    }
    if (QUOTE_START_RES.some((re) => re.test(ln))) {
      if (/^(From|Da|De|Von)\s*:/i.test(ln)) {
        const nxt = lines.slice(i + 1, i + 5).join("\n");
        if (!/^(Sent|Inviato|Envoyé|Gesendet|Enviado|Date|Data|To|A|An|Para|Subject|Oggetto|Objet|Betreff|Asunto)\s*:/im.test(nxt)) continue;
      }
      return i;
    }
    if (QUOTE_OPEN_RES.some((re) => re.test(ln))) {
      const win = lines.slice(i, i + 3).join(" ");
      if (lines.slice(i, i + 3).some((l) => QUOTE_CLOSE_RE.test(l.trim())) && /@|\d/.test(win)) return i;
    }
  }
  return -1;
}

function findSignatureStart(lines) {
  let signoff = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if (SIGNOFF_RE.test(lines[i].trim())) { signoff = i; break; }
  }
  const isStrong = (ln) => SIG_LINE_RE.test(ln) || DISCLAIMER_RE.test(ln) || /\[(photo|icon|image|logo)\]/i.test(ln)
    || (/<https?:/i.test(ln) && /\|/.test(ln)) || (PHONE_RE.test(ln) && /\||\[|<http/.test(ln));
  if (signoff >= 0) {
    let kept = 0;
    for (let i = signoff + 1; i < lines.length; i += 1) {
      const ln = lines[i].trim();
      if (!ln) continue;
      if (isStrong(ln) || /https?:\/\/|www\.|@/.test(ln) || kept >= 2) return i;
      kept += 1;
    }
    return -1;
  }
  for (let i = 0; i < lines.length; i += 1) {
    const ln = lines[i].trim();
    if (!ln) continue;
    if (isStrong(ln) && i > 0) return i;
  }
  return -1;
}

export function splitMessage(raw) {
  const text = normalizeMessageText(raw);
  let lines = text.split("\n");
  let quoted = "";
  const q = findQuoteStart(lines);
  if (q >= 0) {
    quoted = lines.slice(q).join("\n").trim();
    lines = lines.slice(0, q);
  }
  let signature = "";
  const s = findSignatureStart(lines);
  if (s >= 0) {
    signature = lines.slice(s).join("\n").trim();
    lines = lines.slice(0, s);
  }
  return { main: lines.join("\n").trim(), signature, quoted };
}

function hostOf(url) {
  try { return new URL(url.startsWith("www.") ? `http://${url}` : url).hostname.replace(/^www\./, ""); } catch { return url; }
}

export function shortUrlLabel(url) {
  const u = url || "";
  if (/\/api\/unsubscribe/i.test(u)) return "Annulla iscrizione";
  if (/\/privacy/i.test(u)) return "Informativa privacy";
  if (/\.(jpe?g|png|gif|webp|svg)(\?|$)/i.test(u)) return "Immagine";
  const host = hostOf(u);
  for (const k of Object.keys(SOCIAL_LABELS)) {
    if (host === `${k}.com` || host.endsWith(`.${k}.com`)) return SOCIAL_LABELS[k];
  }
  let path = "";
  try { path = new URL(u.startsWith("www.") ? `http://${u}` : u).pathname.replace(/\/$/, ""); } catch { path = ""; }
  if (path && path !== "/" && path.length <= 28) return host + path;
  return host;
}

function labelFor(tag, url) {
  const t = (tag || "").toLowerCase().trim();
  if (/^(photo|image|img|logo|banner|cid:|https?:)/.test(t)) return "Immagine";
  if (t === "icon") return "";
  if (SOCIAL_LABELS[t]) return SOCIAL_LABELS[t];
  if (url && /^mailto:/i.test(url)) return url.replace(/^mailto:/i, "");
  return tag.trim();
}

const TOKEN_RE = new RegExp(
  "\\[([^\\]\\n]{1,120})\\]\\s*<((?:https?|mailto|tel):[^>\\s]+)>" + // [label]<url>
  "|([^\\s<>\\[\\]]+)<((?:https?|mailto|tel):[^>\\s]+)>" + // testo<url>
  "|<((?:https?|mailto|tel):[^>\\s]+)>" + // <url>
  "|\\[icon\\]\\s*" +
  "|(https?://[^\\s<>\"']+)" +
  "|((?<![/@\\w])www\\.[^\\s<>\"']+)" +
  "|([A-Za-z0-9._%+\\-]+@[A-Za-z0-9.\\-]+\\.[A-Za-z]{2,})" +
  "|(?<![\\w/.-])([A-Za-z0-9_-]{48,})(?![\\w/.-])", // token lunghi (JWT, tracker)
  "gi",
);
const TRAIL = ".,;:!?)]}'\"";

function trimTrail(url) {
  let u = url; let trail = "";
  while (u && TRAIL.includes(u[u.length - 1])) { trail = u[u.length - 1] + trail; u = u.slice(0, -1); }
  return [u, trail];
}

// Restituisce una lista di segmenti {type:'text'|'link'|'image', text, href}
export function tokenizeLine(line) {
  const out = [];
  let last = 0;
  const src = line || "";
  TOKEN_RE.lastIndex = 0;
  let m;
  while ((m = TOKEN_RE.exec(src)) !== null) {
    if (m.index > last) out.push({ type: "text", text: src.slice(last, m.index) });
    last = m.index + m[0].length;
    if (m[1] !== undefined) {
      const label = labelFor(m[1], m[2]);
      if (label) out.push({ type: label === "Immagine" ? "image" : "link", text: label, href: m[2] });
    } else if (m[3] !== undefined) {
      const txt = m[3];
      const label = /^(https?:\/\/|www\.)/i.test(txt) ? shortUrlLabel(txt) : txt.replace(/^mailto:/i, "");
      out.push({ type: "link", text: label, href: m[4] });
    } else if (m[5] !== undefined) {
      out.push({ type: /^mailto:/i.test(m[5]) ? "link" : "link", text: /^mailto:/i.test(m[5]) ? m[5].replace(/^mailto:/i, "") : shortUrlLabel(m[5]), href: m[5] });
    } else if (m[6] !== undefined) {
      const [u, trail] = trimTrail(m[6]);
      out.push({ type: "link", text: shortUrlLabel(u), href: u });
      if (trail) out.push({ type: "text", text: trail });
    } else if (m[7] !== undefined) {
      const [u, trail] = trimTrail(m[7]);
      out.push({ type: "link", text: shortUrlLabel(u), href: `http://${u}` });
      if (trail) out.push({ type: "text", text: trail });
    } else if (m[8] !== undefined) {
      out.push({ type: "link", text: m[8], href: `mailto:${m[8]}` });
    } else if (m[9] !== undefined) {
      out.push({ type: "text", text: "…" });
    }
    // m[0] === "[icon] " → nessun output
  }
  if (last < src.length) out.push({ type: "text", text: src.slice(last) });
  return out;
}

export function cleanLine(line) {
  return tokenizeLine(line).map((s) => s.text).join("").replace(/\s{2,}/g, " ").trim();
}

// Anteprima breve (liste): solo il corpo, link mascherati, una riga
export function messagePreview(raw, max = 160) {
  const { main } = splitMessage(raw);
  const base = main || normalizeMessageText(raw);
  const txt = base.split("\n").map((l) => cleanLine(l.replace(/^>+\s?/, ""))).filter(Boolean).join(" ");
  return txt.length > max ? `${txt.slice(0, max - 1)}…` : txt;
}
